import type { UseQueryResult } from '@tanstack/react-query'

import type { FirstPartyTrafficReport, FirstPartyTrafficRow } from '@/api/firstPartyTraffic'
import { deltaPresentation, isNotTracked, notTrackedLast, splitsNotTracked } from '@/components/dataQuality'
import { Card, DataTable, LoadingBlock, type Column } from '@/design'

import { TmtBadge } from './AttributionBadges'
import { AttributionError } from './AttributionStates'
import { NO_TOUCH_LABEL, shareOf, withTrailingUnattributed } from './definitions'
import { Figure, NotTrackedName } from './cells'
import { count, money, share } from './format'

const ZERO_TRAFFIC = { sessions: 0, users: 0, orders: 0, revenueMinor: 0, newCustomers: 0, conversionRate: null, aovMinor: null }

/**
 * Every channel, then the no-touch row last. From an older API it is a zero row when the
 * API sends none (`withTrailingUnattributed`); since the coverage card it is the Not
 * tracked row, last, only as the API sent it — never one made up.
 */
export function channelRows(report: FirstPartyTrafficReport): FirstPartyTrafficRow[] {
  if (splitsNotTracked(report)) return notTrackedLast(report.rows)
  return withTrailingUnattributed(
    report.rows,
    (row) => row.key === null,
    () => ({
      ...ZERO_TRAFFIC,
      key: null,
      label: NO_TOUCH_LABEL,
      path: [],
      model: report.model,
      tmtAttributed: true,
      previous: report.compared ? ZERO_TRAFFIC : null,
    }),
  )
}

function columnsFor(report: FirstPartyTrafficReport): Column<FirstPartyTrafficRow>[] {
  const { currency, totals } = report
  const comparison = deltaPresentation(report.dataQuality)
  return [
    {
      key: 'channel',
      header: 'Channel',
      cell: (row) => (isNotTracked(row) ? <NotTrackedName lookbackDays={report.lookbackDays} /> : row.label),
    },
    {
      key: 'orders',
      header: 'Purchases',
      align: 'end',
      cell: (row) => (
        <Figure value={count(row.orders)} current={row.orders} previous={row.previous?.orders ?? null} comparison={comparison} />
      ),
    },
    {
      key: 'revenue',
      header: 'Revenue',
      align: 'end',
      cell: (row) => (
        <Figure
          value={money(row.revenueMinor, currency)}
          current={row.revenueMinor}
          previous={row.previous?.revenueMinor ?? null}
          comparison={comparison}
        />
      ),
    },
    {
      key: 'share',
      header: 'Share of revenue',
      align: 'end',
      cell: (row) => share(shareOf(row.revenueMinor, totals.revenueMinor)),
    },
    { key: 'newCustomers', header: 'New customers', align: 'end', cell: (row) => count(row.newCustomers) },
  ]
}

/**
 * AC1's channel table, from the Traffic page's first-party source at `level=channel`
 * under the same model and range — production only, like the table above. Its own
 * source badge, since it is a different read: here "unattributed" is strictly no touch
 * in the lookback, and direct visits are a channel of their own.
 */
export function ChannelTable({ result }: { result: UseQueryResult<FirstPartyTrafficReport> }) {
  if (result.isPending) return <LoadingBlock rows={4} label="Loading attributed sales by channel" />
  if (!result.data) return <AttributionError error={result.error} title="Sales by channel could not be loaded" />
  const report = result.data
  return (
    <Card
      title="By channel"
      subtitle={`${report.start} to ${report.end} · production, all websites`}
      actions={<TmtBadge model={report.model} lookbackDays={report.lookbackDays} />}
    >
      <DataTable
        caption="TmT-attributed sales by channel"
        rows={channelRows(report)}
        rowKey={(row) => (isNotTracked(row) ? 'not-tracked' : row.key === null ? 'unattributed' : `key:${row.key}`)}
        columns={columnsFor(report)}
      />
      <p role="note" className="attr-note">
        {splitsNotTracked(report)
          ? `Here a sale with no tracked visit in the ${report.lookbackDays}-day lookback is Not tracked, and direct visits are a channel of their own.`
          : `Here a sale with no touch in the ${report.lookbackDays}-day lookback is unattributed, and direct visits are a channel of their own.`} Creatives are broken down under each channel and campaign on the Website page; Meta ads
        are the Meta ad grouping above.
      </p>
    </Card>
  )
}
